import { Entry } from 'contentful';
import { NextPageWithLayout } from 'next';
import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { BlogPostSection } from '../../components/Blog/BlogPostSection/BlogPostSection';
import { Container } from '../../components/Container/Container';
import { FishingReportsGrid } from '../../components/FishingReportsGrid/FishingReportsGrid';
import { FishingWatersGrid } from '../../components/FishingWatersGrid/FishingWatersGrid';
import { filterFishingWaters } from '../../components/FishingWatersGrid/helpers/filterFishingWaters';
import { Grid } from '../../components/Grid/Grid';
import { HeroMapSection } from '../../components/Maps/components/HeroMap/HeroMapSection';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { PageNavigation } from '../../components/Navigation/PageNavigation/PageNavigation';
import { NearbyPlacesSection } from '../../components/NearbyPlaces/NearbyPlacesSection';
import { RegulationSection } from '../../components/Regulations/RegulationSection';
import { IStateObject } from '../../constants/USstates/constants';
import { getEdgesFromConnection, getNodesFromConnection } from '../../helpers/graphql';
import { useIsTabletScreenWidthOrThinner } from '../../helpers/hooks/useScreenWidth';
import { getNearbyPlacesByDistance } from '../../helpers/places/getNearbyPlacesByDistance';
import { getPlaceSchema } from '../../helpers/schemas/getPlaceSchema';
import { getPlaceUrl } from '../../helpers/urls';
import { TypeBlogPostSkeleton } from '../../interfaces/contentful';
import { IBoundingBox, ICoordinates } from '../../interfaces/geo';
import {
  GetPlace_entry,
  GetPlace_entry_catches_edges_node,
  GetPlaceNearbyPlaces_entry_nearbyPlaces,
  GetPlaceTopAnglers_entry_topAnglers,
  GetPlaceTopSpecies_entry_topSpeciesSummary,
  GetPlaceTopWaters_entry,
} from '../../interfaces/graphql';
import { FAQSection } from './components/FAQSection/FAQSection';
import { GeneralInfoSection } from './components/GeneralInfoSection/GeneralInfoSection';
import { PreFooterLinksSection } from './components/PreFooterLinksSection/PreFooterLinksSection';
import { TopSpeciesSection } from './components/TopSpeciesSection/TopSpeciesSection';
import { TrophyCatchesAppDownloadSection } from './components/TrophyCatchesAppDownloadSection/TrophyCatchesAppDownloadSection';
import { WeatherSection } from './components/WeatherSection/WeatherSection';
import { getPlaceSubNavLinks, PLACE_SUB_LINK_IDS } from './helpers/getPlaceSubNavLinks';

export interface ITopSpeciesFishingSpots {
  speciesId: string;
  nrOfWaters?: number;
}

export interface IPlaceProps {
  boundingBox: IBoundingBox | null;
  blogPosts: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[] | null;
  nearbyPlaces: GetPlaceNearbyPlaces_entry_nearbyPlaces | null;
  place: GetPlace_entry;
  coordinates: ICoordinates;
  states: IStateObject[];
  topWaters: GetPlaceTopWaters_entry | null;
  topAnglers: GetPlaceTopAnglers_entry_topAnglers | null;
  topAnglersDateString: string;
  topSpeciesSummary: GetPlaceTopSpecies_entry_topSpeciesSummary | null;
  topSpeciesFishingSpots: ITopSpeciesFishingSpots[] | null;
}

export const Place: NextPageWithLayout<IPlaceProps> = ({
  boundingBox,
  blogPosts,
  nearbyPlaces,
  place,
  coordinates,
  states,
  topWaters,
  topAnglers,
  topAnglersDateString,
  topSpeciesSummary,
  topSpeciesFishingSpots,
}) => {
  const { _id, name, catches, country, region } = place;
  const isTabletScreenWidthOrThinner = useIsTabletScreenWidthOrThinner();
  const slug = place.slug ?? '';
  const { id: countryCode, countryName } = country;
  const regionCode = region.regionCode ?? '';

  const topSpecies = getNodesFromConnection(topSpeciesSummary);
  const top3SpeciesNames = topSpecies.slice(0, 3).map(({ displayName }) => displayName);
  const topSpeciesId = topSpecies[0]?._id;

  const title = regionCode
    ? `Fishing in ${name}, ${regionCode.toUpperCase()} (${countryName}) ➡️️️️ Find Spots, Species & Tips`
    : `Fishing in ${name}, (${country.countryName}) ➡️️️ Find Spots, Species & Tips`;

  const metaDescription = regionCode
    ? `Fishing in ${name}, ${region.name} (${countryName})? Discover the finest fishing spots in ${name}, ${regionCode.toUpperCase()} → top species caught, tips, and more 🐟 Fishbrain`
    : `Fishing in ${name}, (${country.countryName})? Discover the finest fishing spots in ${name} → top species caught, tips, and more 🐟 Fishbrain`;

  const topSpeciesCount = topSpeciesSummary?.edges?.length ?? 0;

  const nearbyPlacesEdges = getEdgesFromConnection(nearbyPlaces);
  const nearbyPlacesNodes = getNodesFromConnection(nearbyPlaces);
  const nearbyPlacesDistanceUnit = nearbyPlacesEdges[0]?.distanceUnit;
  const nearbyPlacesArray = nearbyPlaces
    ? getNearbyPlacesByDistance({
        nearbyPlaces: nearbyPlacesNodes,
        distanceUnit: nearbyPlacesDistanceUnit,
        mainLatitude: coordinates.latitude,
        mainLongitude: coordinates.longitude,
      })
    : [];

  const recentCatchPostList = getNodesFromConnection<GetPlace_entry_catches_edges_node>(
    catches,
  ).map(_catch => _catch.post);

  const catchCount = recentCatchPostList.length;
  const filteredWaters = getNodesFromConnection(topWaters).filter(filterFishingWaters);

  const hasCatches = catchCount > 0;
  const hasGeneralInfo = isTabletScreenWidthOrThinner;
  const showRegulations = country.id.toLowerCase() === 'us';
  const hasSpecies = topSpeciesCount > 0;
  const hasNearbyCities = nearbyPlacesArray.length > 0;

  const hasFishingSpots = filteredWaters.length > 0;

  return (
    <div>
      <MetaTags
        link={getPlaceUrl(countryCode, regionCode, slug)}
        title={title}
        description={metaDescription}
        schema={getPlaceSchema({
          name,
          countryId: countryCode,
          regionCode,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          slug,
        })}
      />
      <PageNavigation
        navItems={getPlaceSubNavLinks({
          hasCatches,
          hasGeneralInfo,
          hasRegulations: showRegulations,
          hasSpecies,
          hasFishingSpots,
          hasNearbyCities,
        })}
      />
      {/* Map section */}
      <HeroMapSection
        country={country}
        region={region}
        id={PLACE_SUB_LINK_IDS.MAP}
        longitude={coordinates.longitude}
        latitude={coordinates.latitude}
        title={`Fishing in ${name}, ${regionCode}`}
        sourceId={_id}
        zoom={10}
      />
      {/* Header section */}
      <Container className="px-0">
        <Grid gapType="custom" className="grid-flow-row gap-0">
          {hasFishingSpots && (
            <FishingWatersGrid
              className="order-1 col-span-full mb-12 sm:mb-16 md:order-4 md:mb-28"
              id={PLACE_SUB_LINK_IDS.FISHING_SPOTS}
              fishingWaters={filteredWaters}
              fakeFilters={top3SpeciesNames}
              title={`Best fishing spots in ${name}, ${regionCode}`}
              sourceId={_id}
            />
          )}
          <TrophyCatchesAppDownloadSection
            className={`order-2 mb-12 sm:mb-16 md:order-3 ${hasSpecies ? 'md:col-span-7' : 'md:col-span-5'} md:mb-28 md:pr-5`}
            displayName={name}
          />
          {hasSpecies && (
            <TopSpeciesSection
              boundingBox={boundingBox ?? undefined}
              className="order-3 col-span-full mb-12 px-6 sm:mx-16 sm:mb-16 sm:px-8 sm:pb-2 sm:pt-6 md:order-2 md:col-span-5 md:row-span-3 md:mx-0 md:mb-28"
              placeId={_id}
              placeName={name}
              topSpeciesSummary={topSpeciesSummary ?? undefined}
              topSpeciesFishingSpots={topSpeciesFishingSpots ?? []}
            />
          )}
          {hasCatches && (
            <FishingReportsGrid
              className="mb-fw order-4 col-span-full md:order-5"
              id={PLACE_SUB_LINK_IDS.FISHING_REPORTS}
              fishingReports={recentCatchPostList}
              showFishingWaterName
              title={`Latest ${name} fishing reports`}
            />
          )}
          <GeneralInfoSection
            className={`order-5 col-span-full mb-12 sm:mb-16 md:order-1 md:col-span-7 md:mb-28 md:mt-0 md:pr-5 ${hasSpecies ? 'md:mb-5' : ''}`}
            country={country}
            placeName={name}
            region={region}
            topAnglers={topAnglers?.anglers ?? null}
            topAnglersDateString={topAnglersDateString}
            topSpeciesSummary={topSpeciesSummary ?? undefined}
          />
        </Grid>
      </Container>
      {/* weather section */}
      <WeatherSection
        coordinates={coordinates}
        id={PLACE_SUB_LINK_IDS.WEATHER}
        title={`7-day weather forecast in ${name}`}
        className="mb-fw"
        topSpeciesSummary={topSpeciesSummary ?? undefined}
      />
      {/* regulation section */}
      {showRegulations && (
        <RegulationSection
          className="mb-fw !mt-0"
          title={`Fishing regulations in ${name}`}
          coordinates={coordinates}
          topSpeciesId={topSpeciesId}
          region={region}
          id={PLACE_SUB_LINK_IDS.FISHING_REGULATIONS}
          intersectionObserverOptions={{ rootMargin: '0px' }}
        />
      )}

      {blogPosts && blogPosts.length > 0 && (
        <BlogPostSection
          className="mb-fw !mt-0"
          containerVariant="thin"
          blogPostItems={blogPosts}
          linkTo="/blog"
          linkToText="See all blog posts"
          placeName={name}
        />
      )}
      {/* AppDownload CTA Section */}
      <AppDownloadSection className="mb-fw !py-0" hasAppRating />
      {/* FAQ Section */}
      <FAQSection
        country={country}
        id={PLACE_SUB_LINK_IDS.FAQ}
        region={region}
        recentCatchPostList={recentCatchPostList}
        placeId={_id}
        placeName={name}
        placeSlug={slug}
        topWaters={filteredWaters}
        topSpeciesSummary={topSpeciesSummary ?? undefined}
      />
      {/* Cities nearby Section */}
      {nearbyPlacesArray.length > 0 && (
        <NearbyPlacesSection
          id={PLACE_SUB_LINK_IDS.NEARBY_CITIES}
          distanceUnit={nearbyPlacesDistanceUnit}
          nearbyPlaces={nearbyPlacesArray}
          mainLatitude={coordinates.latitude}
          mainLongitude={coordinates.longitude}
          title={`Other cities near ${name}`}
        />
      )}
      {/* PrefooterLinks SEO Section */}
      <PreFooterLinksSection country={country} states={states} />
    </div>
  );
};
